import { useState, useEffect, useCallback, useMemo } from "react";
import { recordUserAction } from "../utils/awsRum";
import { usePageLoadMetric } from "../hooks/usePageLoadMetric";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import clsx from "clsx";
import { DndContext, type DragEndEvent } from "@dnd-kit/core";

import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { ActionIcon } from "../components/atoms/Icon";
import { Button } from "../components/atoms";
import { Tag } from "../components/atoms/Tag/Tag";
import { resolveHex } from "../styles/colors";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import { fetchEmployees, deleteEmployee, updateEmployeeTransfer, type EmployeeResponse } from "../api/employees";
import { fetchStoreDepartments, type DepartmentResponse } from "../api/departments";
import { fetchStores } from "../api/stores";
import { EmployeeTreeView } from "../components/molecules/EmployeeTreeView";
import type { RootState } from "../app/store";
import { ALL_SELECTION_ID } from "../app/store/storeSelectionSlice";
import { resolveStoreIdsForSelection } from "../shared/store-selection/resolveStoreIds";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableEditHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
} from "../components/table";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";

// --- ヘルパー関数群 ---

const EmploymentStatusBadge = ({ value }: { value: string }) => {
    const getBackgroundColor = (status: string): string => {
        switch (status) {
            case "在籍中": return "#D1A1A1";
            case "休職": return "#D3CF9F";
            case "退職": return "#DDD9DC";
            case "復職予定": return "#A8BDCA";
            default: return "#E5E7EB";
        }
    };
    return (
        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-800" style={{ backgroundColor: getBackgroundColor(value) }}>
            {value}
        </span>
    );
};

const EMPLOYMENT_TYPE_MAP: Record<string, string> = { "1": "正社員", "2": "契約社員", "3": "パート", "4": "アルバイト" };

const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";
const TAG_SEPARATOR = ", ";

type SortColumn =
    | "id" | "name" | "nickname" | "store" | "dept"
    | "status" | "empType" | "role" | "birth"
    | "nationality" | "tags" | "memo";

const toKey = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : val;

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

const empTagsToKey = (tags: { name: string | null }[]): string =>
    tags.length === 0 ? EMPTY_VALUE_KEY : tags.map((t) => t.name ?? "").join(TAG_SEPARATOR);

const EmploymentTypeBadge = ({ value }: { value: string }) => {
    const label = EMPLOYMENT_TYPE_MAP[value] || value;
    const getBorderColor = (type: string): string => {
        switch (type) {
            case "正社員": return "#D1A1A1";
            case "契約社員": return "#A8BDCA";
            case "パート": return "#D3CF9F";
            case "アルバイト": return "#D3CF9F";
            default: return "#DDD9DC";
        }
    };
    return (
        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-800 bg-white border-2" style={{ borderColor: getBorderColor(label) }}>
            {label}
        </span>
    );
};



type ViewMode = "list" | "tree";
const ViewToggle = ({ viewMode, onChange }: { viewMode: ViewMode; onChange: (mode: ViewMode) => void }) => (
    <div className="inline-flex rounded-lg overflow-hidden border border-gray-200">
        <button type="button" onClick={() => onChange("list")} className={clsx("p-2 transition-colors", viewMode === "list" ? "bg-brand-500 text-white" : "bg-white text-gray-400 hover:text-gray-600")}>
            <ListIcon className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => onChange("tree")} className={clsx("p-2 transition-colors", viewMode === "tree" ? "bg-brand-500 text-white" : "bg-white text-gray-400 hover:text-gray-600")}>
            <GridIcon className="w-5 h-5" />
        </button>
    </div>
);

// --- メインコンポーネント ---
const EmployeesPage = () => {
    const { t } = useTranslation("common");
    const menuTitle = useMenuTitle();
    const navigate = useNavigate();
    const storeSelection = useSelector((state: RootState) => state.storeSelection);
    const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
    const [departments, setDepartments] = useState<DepartmentResponse[]>([]);
    const [storeNameMap, setStoreNameMap] = useState<Record<number, string>>({});
    const [departmentNameMap, setDepartmentNameMap] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    usePageLoadMetric("employees", !loading);
    const [viewMode, setViewMode] = useState<ViewMode>("list");

    const [deleteTarget, setDeleteTarget] = useState<EmployeeResponse | null>(null);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [transferData, setTransferData] = useState<{
        employee: EmployeeResponse;
        fromDeptName: string;
        toDeptId: number;
        toDeptName: string;
    } | null>(null);

    const selectedStoreId = storeSelection.store?.id;
    const isAllStores = storeSelection.store?.id === ALL_SELECTION_ID;
    const isAllDepartments = storeSelection.department?.id === ALL_SELECTION_ID;
    const selectedDepartmentId = storeSelection.department?.id;

    const loadEmployees = useCallback(async (keyword?: string) => {
        setLoading(true);
        setError(null);
        try {
            // 「全店舗」モード: エリア配下の全店舗IDを解決してstore_idsで一括取得
            if (isAllStores) {
                const storeIds = await resolveStoreIdsForSelection(storeSelection);
                const response = await fetchEmployees({
                    keyword: keyword || undefined,
                    page_size: 100,
                    store_ids: storeIds.join(","),
                    department_id: !isAllDepartments && selectedDepartmentId ? selectedDepartmentId : undefined,
                });
                setEmployees(response.items);
            } else if (selectedStoreId) {
                // 特定店舗が選択されている場合
                const response = await fetchEmployees({
                    keyword: keyword || undefined,
                    page_size: 100,
                    store_id: selectedStoreId,
                    department_id: !isAllDepartments && selectedDepartmentId ? selectedDepartmentId : undefined,
                });
                setEmployees(response.items);
            } else {
                // 店舗未選択（エリアのみ）の場合: 全取得 + クライアントフィルタ
                const response = await fetchEmployees({ keyword: keyword || undefined, page_size: 100 });
                let items = response.items;
                const areaStoreIds = await resolveStoreIdsForSelection(storeSelection);
                if (areaStoreIds.length > 0) {
                    const storeIdSet = new Set(areaStoreIds);
                    items = items.filter((emp) => storeIdSet.has(emp.store_id));
                }
                setEmployees(items);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : `${t("EMPLOYEE")}データの取得に失敗しました`);
        } finally {
            setLoading(false);
        }
    }, [selectedStoreId, storeSelection, isAllStores, isAllDepartments, selectedDepartmentId]);

    useEffect(() => { loadEmployees(); }, [loadEmployees]);

    useEffect(() => {
        const loadStoreNames = async () => {
            const response = await fetchStores();
            const map: Record<number, string> = {};
            response.items.forEach(s => map[s.id] = s.name);
            setStoreNameMap(map);
        };
        loadStoreNames();
    }, []);

    useEffect(() => {
        if (employees.length === 0) {return;}
        const uniqueStoreIds = [...new Set(employees.map((e) => e.store_id))];
        const loadDepartmentNames = async () => {
            const map: Record<number, string> = {};
            await Promise.all(uniqueStoreIds.map(async (id) => {
                const response = await fetchStoreDepartments(id);
                response.items.forEach(d => map[d.id] = d.name);
            }));
            setDepartmentNameMap(map);
        };
        loadDepartmentNames();
    }, [employees]);

    useEffect(() => {
        if (viewMode !== "tree") { setDepartments([]); return; }
        // 「全店舗」モード: 全従業員のdepartment_nameからユニークな部門を構築
        if (isAllStores) {
            const seen = new Map<number, DepartmentResponse>();
            for (const emp of employees) {
                if (!seen.has(emp.department_id)) {
                    seen.set(emp.department_id, {
                        id: emp.department_id,
                        organization_id: emp.organization_id ?? 0,
                        code: "",
                        name: emp.department_name ?? `部門${emp.department_id}`,
                    });
                }
            }
            setDepartments([...seen.values()]);
            return;
        }
        if (!selectedStoreId) { setDepartments([]); return; }
        const loadDepartments = async () => {
            const response = await fetchStoreDepartments(selectedStoreId);
            setDepartments(response.items);
        };
        loadDepartments();
    }, [viewMode, selectedStoreId, isAllStores, employees]);

    const departmentGroups = useMemo(() => {
        return departments.map(dept => ({
            department: dept,
            employees: employees.filter(emp => emp.department_id === dept.id)
        }));
    }, [departments, employees]);

    const unassignedEmployees = useMemo(() => {
        const deptIds = new Set(departments.map(d => d.id));
        return employees.filter(emp => !deptIds.has(emp.department_id));
    }, [departments, employees]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (!over) return;

        const employee = active.data.current as EmployeeResponse;
        const toDeptId = over.id as number;

        if (employee.department_id === toDeptId) return;
        if (toDeptId === -1) return;

        const fromDept = departments.find(d => d.id === employee.department_id);
        const fromDeptName = fromDept?.name ?? "未分類";
        const toDept = departments.find(d => d.id === toDeptId);
        const toDeptName = toDept?.name ?? "不明";

        setTransferData({ employee, fromDeptName, toDeptId, toDeptName });
        setIsTransferModalOpen(true);
    }, [departments]);

    const handleTransferConfirm = async (employeeId: number, toDeptId: number, date: string) => {
        recordUserAction("employee_transferred");
        try {
            await updateEmployeeTransfer(employeeId, { department_id: toDeptId, effective_date: date });
            setIsTransferModalOpen(false);
            setTransferData(null);
            loadEmployees();
        } catch (err) {
            alert(err instanceof Error ? err.message : "エラーが発生しました");
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        recordUserAction("employee_deleted");
        try {
            await deleteEmployee(deleteTarget.id);
            setDeleteTarget(null);
            loadEmployees();
        } catch (err) {
            alert(err instanceof Error ? err.message : "削除に失敗しました");
        }
    };

    const displayValue = (val: string | number | null | undefined) => (val === null || val === undefined || val === "" ? "-" : String(val));
    const formatDate = (dateStr: string | null) => dateStr ? format(new Date(dateStr), "yyyy年M月d日") : "-";

    // --- リスト表示用 並び替え/絞り込み state ---

    const codeFilter = useColumnFilter(employees, (e) => toKey(e.code), labelOrEmpty);
    const nameFilter = useColumnFilter(employees, (e) => toKey(e.name), labelOrEmpty);
    const nicknameFilter = useColumnFilter(employees, (e) => toKey(e.nickname), labelOrEmpty);
    const storeFilter = useColumnFilter(
        employees,
        (e) => storeNameMap[e.store_id] ?? EMPTY_VALUE_KEY,
        labelOrEmpty,
    );
    const deptFilter = useColumnFilter(
        employees,
        (e) => departmentNameMap[e.department_id] ?? toKey(e.department_name),
        labelOrEmpty,
    );
    const statusFilter = useColumnFilter(employees, (e) => toKey(e.employment_status), labelOrEmpty);
    const empTypeFilter = useColumnFilter(
        employees,
        (e) => String(e.employment_type_id),
        (k) => EMPLOYMENT_TYPE_MAP[k] ?? EMPTY_VALUE_LABEL,
    );
    const roleFilter = useColumnFilter(employees, (e) => toKey(e.role), labelOrEmpty);
    const birthFilter = useColumnFilter(
        employees,
        (e) => toKey(e.birth_date),
        (k) => (k === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : format(new Date(k), "yyyy年M月d日")),
    );
    const nationalityFilter = useColumnFilter(employees, (e) => toKey(e.nationality), labelOrEmpty);
    const tagsFilter = useColumnFilter(employees, (e) => empTagsToKey(e.tags), labelOrEmpty);
    const memoFilter = useColumnFilter(employees, (e) => toKey(e.memo), labelOrEmpty);

    // 並び替えは「同時に1列のみ」アクティブ
    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    // 表示用にソート/絞り込みを適用
    const displayedEmployees = useMemo(() => {
        let list = employees;

        if (codeFilter.isFiltering) {
            const set = new Set(codeFilter.selected);
            list = list.filter((e) => set.has(toKey(e.code)));
        }
        if (nameFilter.isFiltering) {
            const set = new Set(nameFilter.selected);
            list = list.filter((e) => set.has(toKey(e.name)));
        }
        if (nicknameFilter.isFiltering) {
            const set = new Set(nicknameFilter.selected);
            list = list.filter((e) => set.has(toKey(e.nickname)));
        }
        if (storeFilter.isFiltering) {
            const set = new Set(storeFilter.selected);
            list = list.filter((e) => set.has(storeNameMap[e.store_id] ?? EMPTY_VALUE_KEY));
        }
        if (deptFilter.isFiltering) {
            const set = new Set(deptFilter.selected);
            list = list.filter((e) =>
                set.has(departmentNameMap[e.department_id] ?? toKey(e.department_name)),
            );
        }
        if (statusFilter.isFiltering) {
            const set = new Set(statusFilter.selected);
            list = list.filter((e) => set.has(toKey(e.employment_status)));
        }
        if (empTypeFilter.isFiltering) {
            const set = new Set(empTypeFilter.selected);
            list = list.filter((e) => set.has(String(e.employment_type_id)));
        }
        if (roleFilter.isFiltering) {
            const set = new Set(roleFilter.selected);
            list = list.filter((e) => set.has(toKey(e.role)));
        }
        if (birthFilter.isFiltering) {
            const set = new Set(birthFilter.selected);
            list = list.filter((e) => set.has(toKey(e.birth_date)));
        }
        if (nationalityFilter.isFiltering) {
            const set = new Set(nationalityFilter.selected);
            list = list.filter((e) => set.has(toKey(e.nationality)));
        }
        if (tagsFilter.isFiltering) {
            const set = new Set(tagsFilter.selected);
            list = list.filter((e) => set.has(empTagsToKey(e.tags)));
        }
        if (memoFilter.isFiltering) {
            const set = new Set(memoFilter.selected);
            list = list.filter((e) => set.has(toKey(e.memo)));
        }

        if (activeSort) {
            const cmp = (a: EmployeeResponse, b: EmployeeResponse): number => {
                switch (activeSort.column) {
                    case "id":
                        return (a.code ?? "").localeCompare(b.code ?? "", "ja");
                    case "name":
                        return (a.name ?? "").localeCompare(b.name ?? "", "ja");
                    case "nickname":
                        return (a.nickname ?? "").localeCompare(b.nickname ?? "", "ja");
                    case "store":
                        return (storeNameMap[a.store_id] ?? "").localeCompare(
                            storeNameMap[b.store_id] ?? "",
                            "ja",
                        );
                    case "dept":
                        return (departmentNameMap[a.department_id] ?? a.department_name ?? "").localeCompare(
                            departmentNameMap[b.department_id] ?? b.department_name ?? "",
                            "ja",
                        );
                    case "status":
                        return (a.employment_status ?? "").localeCompare(b.employment_status ?? "", "ja");
                    case "empType":
                        return (EMPLOYMENT_TYPE_MAP[String(a.employment_type_id)] ?? "").localeCompare(
                            EMPLOYMENT_TYPE_MAP[String(b.employment_type_id)] ?? "",
                            "ja",
                        );
                    case "role":
                        return (a.role ?? "").localeCompare(b.role ?? "", "ja");
                    case "birth":
                        return (a.birth_date ?? "").localeCompare(b.birth_date ?? "", "ja");
                    case "nationality":
                        return (a.nationality ?? "").localeCompare(b.nationality ?? "", "ja");
                    case "tags":
                        return empTagsToKey(a.tags).localeCompare(empTagsToKey(b.tags), "ja");
                    case "memo":
                        return (a.memo ?? "").localeCompare(b.memo ?? "", "ja");
                }
            };
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        employees,
        codeFilter.isFiltering, codeFilter.selected,
        nameFilter.isFiltering, nameFilter.selected,
        nicknameFilter.isFiltering, nicknameFilter.selected,
        storeFilter.isFiltering, storeFilter.selected, storeNameMap,
        deptFilter.isFiltering, deptFilter.selected, departmentNameMap,
        statusFilter.isFiltering, statusFilter.selected,
        empTypeFilter.isFiltering, empTypeFilter.selected,
        roleFilter.isFiltering, roleFilter.selected,
        birthFilter.isFiltering, birthFilter.selected,
        nationalityFilter.isFiltering, nationalityFilter.selected,
        tagsFilter.isFiltering, tagsFilter.selected,
        memoFilter.isFiltering, memoFilter.selected,
        activeSort,
    ]);

    // ヘッダークリック時、ポップアップは「同時に1つだけ」開く
    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const map = {
                id: codeFilter,
                name: nameFilter,
                nickname: nicknameFilter,
                store: storeFilter,
                dept: deptFilter,
                status: statusFilter,
                empType: empTypeFilter,
                role: roleFilter,
                birth: birthFilter,
                nationality: nationalityFilter,
                tags: tagsFilter,
                memo: memoFilter,
            } as const;
            const target = map[which];
            target.anchorRef.current = anchor;
            const willOpen = !target.open;
            target.setOpen(willOpen);
            if (willOpen) {
                Object.entries(map).forEach(([col, f]) => {
                    if (col !== which) f.setOpen(false);
                });
            }
        },
        [codeFilter, nameFilter, nicknameFilter, storeFilter, deptFilter, statusFilter, empTypeFilter, roleFilter, birthFilter, nationalityFilter, tagsFilter, memoFilter],
    );

    return (
        <>
            <div className="flex-1 flex flex-col overflow-hidden">
                <PageHeader title={menuTitle} rightContent={<ViewToggle viewMode={viewMode} onChange={setViewMode} />} />
                <TabBar />

                {viewMode === "list" && (
                    <div className="px-6 pb-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button onClick={() => navigate("/employees/new")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors">
                                <ActionIcon name="add" className="w-4 h-4" />新規登録
                            </button>
                            <button type="button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors">
                                <ActionIcon name="edit" className="w-4 h-4" />一括編集
                            </button>
                            <button type="button" disabled className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed">
                                <ActionIcon name="upload" className="w-4 h-4" />アップロード
                            </button>
                            <button type="button" disabled className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed">
                                <ActionIcon name="download" className="w-4 h-4" />ダウンロード
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex-1 min-h-0 px-6 pb-6">
                    {viewMode === "tree" ? (
                        !storeSelection.store ? (
                            <div className="flex items-center justify-center h-64 text-sm text-gray-500">店舗を選択してください</div>
                        ) : (
                            <div className="overflow-x-auto overflow-y-auto max-h-full">
                                <DndContext onDragEnd={handleDragEnd}>
                                    <EmployeeTreeView
                                        storeName={isAllStores ? "全店舗" : storeSelection.store.name}
                                        departmentGroups={departmentGroups}
                                        unassignedEmployees={unassignedEmployees}
                                        onEmployeeClick={(emp) => navigate(`/employees/${emp.id}/edit`)}
                                        onEmployeeDelete={(emp) => setDeleteTarget(emp)}
                                        onAddEmployee={() => navigate("/employees/new")}
                                    />
                                </DndContext>
                            </div>
                        )
                    ) : (
                        /* リスト表示 */
                        <CommonTable>
                            <thead className="sticky top-0 z-20">
                                <tr>
                                    <CommonTableEditHeaderCell className="sticky left-0 z-30" />
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={codeFilter.open}
                                        sortDirection={sortFor("id")}
                                        filterCount={codeFilter.filterCount}
                                        onSort={(anchor) => openOnly("id", anchor)}
                                        className="min-w-[140px]"
                                    >ID</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={nameFilter.open}
                                        sortDirection={sortFor("name")}
                                        filterCount={nameFilter.filterCount}
                                        onSort={(anchor) => openOnly("name", anchor)}
                                        className="min-w-[120px]"
                                    >名前</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={nicknameFilter.open}
                                        sortDirection={sortFor("nickname")}
                                        filterCount={nicknameFilter.filterCount}
                                        onSort={(anchor) => openOnly("nickname", anchor)}
                                        className="min-w-[96px]"
                                    >略称</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={storeFilter.open}
                                        sortDirection={sortFor("store")}
                                        filterCount={storeFilter.filterCount}
                                        onSort={(anchor) => openOnly("store", anchor)}
                                        className="min-w-[112px]"
                                    >店舗</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={deptFilter.open}
                                        sortDirection={sortFor("dept")}
                                        filterCount={deptFilter.filterCount}
                                        onSort={(anchor) => openOnly("dept", anchor)}
                                        className="min-w-[96px]"
                                    >部門</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={statusFilter.open}
                                        sortDirection={sortFor("status")}
                                        filterCount={statusFilter.filterCount}
                                        onSort={(anchor) => openOnly("status", anchor)}
                                        className="min-w-[112px]"
                                    >在籍状況</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={empTypeFilter.open}
                                        sortDirection={sortFor("empType")}
                                        filterCount={empTypeFilter.filterCount}
                                        onSort={(anchor) => openOnly("empType", anchor)}
                                        className="min-w-[112px]"
                                    >雇用形態</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={roleFilter.open}
                                        sortDirection={sortFor("role")}
                                        filterCount={roleFilter.filterCount}
                                        onSort={(anchor) => openOnly("role", anchor)}
                                        className="min-w-[96px]"
                                    >役割</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={birthFilter.open}
                                        sortDirection={sortFor("birth")}
                                        filterCount={birthFilter.filterCount}
                                        onSort={(anchor) => openOnly("birth", anchor)}
                                        className="min-w-[128px]"
                                    >生年月日</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={nationalityFilter.open}
                                        sortDirection={sortFor("nationality")}
                                        filterCount={nationalityFilter.filterCount}
                                        onSort={(anchor) => openOnly("nationality", anchor)}
                                        className="min-w-[96px]"
                                    >国籍</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={tagsFilter.open}
                                        sortDirection={sortFor("tags")}
                                        filterCount={tagsFilter.filterCount}
                                        onSort={(anchor) => openOnly("tags", anchor)}
                                        className="min-w-[160px]"
                                    >タグ</CommonTableHeaderCell>
                                    <CommonTableHeaderCell
                                        sortable
                                        sortActive={memoFilter.open}
                                        sortDirection={sortFor("memo")}
                                        filterCount={memoFilter.filterCount}
                                        onSort={(anchor) => openOnly("memo", anchor)}
                                        className="min-w-[200px]"
                                    >備考</CommonTableHeaderCell>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedEmployees.map((emp) => (
                                    <CommonTableRow key={emp.id}>
                                        <CommonTableEditCell
                                            className="sticky left-0 z-10 bg-[#E9F1F2]"
                                            onClick={() => navigate(`/employees/${emp.id}/edit`)}
                                        />
                                        <CommonTableCell>{displayValue(emp.code)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(emp.name)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(emp.nickname)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(storeNameMap[emp.store_id])}</CommonTableCell>
                                        <CommonTableCell>{displayValue(departmentNameMap[emp.department_id])}</CommonTableCell>
                                        <CommonTableCell>{emp.employment_status ? <EmploymentStatusBadge value={emp.employment_status} /> : "-"}</CommonTableCell>
                                        <CommonTableCell>{EMPLOYMENT_TYPE_MAP[String(emp.employment_type_id)] ?? "-"}</CommonTableCell>
                                        <CommonTableCell>{displayValue(emp.role)}</CommonTableCell>
                                        <CommonTableCell>{formatDate(emp.birth_date)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(emp.nationality)}</CommonTableCell>
                                        <CommonTableCell>
                                            <div className="flex flex-wrap items-center justify-center gap-1">
                                                {emp.tags.length > 0 ? (
                                                    emp.tags.map((tag) => (
                                                        <Tag
                                                            key={tag.id}
                                                            label={tag.name ?? ""}
                                                            color={resolveHex(tag.color ?? "")}
                                                        />
                                                    ))
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </div>
                                        </CommonTableCell>
                                        <CommonTableCell>{displayValue(emp.memo)}</CommonTableCell>
                                    </CommonTableRow>
                                ))}
                            </tbody>
                        </CommonTable>
                    )}
                </div>
            </div>

            {/* 列ごとの並び替え＋絞り込みポップアップ（リスト表示用） */}
            <ColumnFilterPopup
                isOpen={codeFilter.open}
                onClose={() => codeFilter.setOpen(false)}
                anchorRef={codeFilter.anchorRef}
                sortDirection={sortFor("id")}
                onSortChange={handleSortChange("id")}
                options={codeFilter.options}
                selectedValues={codeFilter.selected}
                onSelectedValuesChange={codeFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={nameFilter.open}
                onClose={() => nameFilter.setOpen(false)}
                anchorRef={nameFilter.anchorRef}
                sortDirection={sortFor("name")}
                onSortChange={handleSortChange("name")}
                options={nameFilter.options}
                selectedValues={nameFilter.selected}
                onSelectedValuesChange={nameFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={nicknameFilter.open}
                onClose={() => nicknameFilter.setOpen(false)}
                anchorRef={nicknameFilter.anchorRef}
                sortDirection={sortFor("nickname")}
                onSortChange={handleSortChange("nickname")}
                options={nicknameFilter.options}
                selectedValues={nicknameFilter.selected}
                onSelectedValuesChange={nicknameFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={storeFilter.open}
                onClose={() => storeFilter.setOpen(false)}
                anchorRef={storeFilter.anchorRef}
                sortDirection={sortFor("store")}
                onSortChange={handleSortChange("store")}
                options={storeFilter.options}
                selectedValues={storeFilter.selected}
                onSelectedValuesChange={storeFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={deptFilter.open}
                onClose={() => deptFilter.setOpen(false)}
                anchorRef={deptFilter.anchorRef}
                sortDirection={sortFor("dept")}
                onSortChange={handleSortChange("dept")}
                options={deptFilter.options}
                selectedValues={deptFilter.selected}
                onSelectedValuesChange={deptFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={statusFilter.open}
                onClose={() => statusFilter.setOpen(false)}
                anchorRef={statusFilter.anchorRef}
                sortDirection={sortFor("status")}
                onSortChange={handleSortChange("status")}
                options={statusFilter.options}
                selectedValues={statusFilter.selected}
                onSelectedValuesChange={statusFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={empTypeFilter.open}
                onClose={() => empTypeFilter.setOpen(false)}
                anchorRef={empTypeFilter.anchorRef}
                sortDirection={sortFor("empType")}
                onSortChange={handleSortChange("empType")}
                options={empTypeFilter.options}
                selectedValues={empTypeFilter.selected}
                onSelectedValuesChange={empTypeFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={roleFilter.open}
                onClose={() => roleFilter.setOpen(false)}
                anchorRef={roleFilter.anchorRef}
                sortDirection={sortFor("role")}
                onSortChange={handleSortChange("role")}
                options={roleFilter.options}
                selectedValues={roleFilter.selected}
                onSelectedValuesChange={roleFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={birthFilter.open}
                onClose={() => birthFilter.setOpen(false)}
                anchorRef={birthFilter.anchorRef}
                sortDirection={sortFor("birth")}
                onSortChange={handleSortChange("birth")}
                options={birthFilter.options}
                selectedValues={birthFilter.selected}
                onSelectedValuesChange={birthFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={nationalityFilter.open}
                onClose={() => nationalityFilter.setOpen(false)}
                anchorRef={nationalityFilter.anchorRef}
                sortDirection={sortFor("nationality")}
                onSortChange={handleSortChange("nationality")}
                options={nationalityFilter.options}
                selectedValues={nationalityFilter.selected}
                onSelectedValuesChange={nationalityFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={tagsFilter.open}
                onClose={() => tagsFilter.setOpen(false)}
                anchorRef={tagsFilter.anchorRef}
                sortDirection={sortFor("tags")}
                onSortChange={handleSortChange("tags")}
                options={tagsFilter.options}
                selectedValues={tagsFilter.selected}
                onSelectedValuesChange={tagsFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={memoFilter.open}
                onClose={() => memoFilter.setOpen(false)}
                anchorRef={memoFilter.anchorRef}
                sortDirection={sortFor("memo")}
                onSortChange={handleSortChange("memo")}
                options={memoFilter.options}
                selectedValues={memoFilter.selected}
                onSelectedValuesChange={memoFilter.setSelected}
            />

            {/* 異動確認モーダル */}
            {isTransferModalOpen && transferData && (
                <TransferModal data={transferData} onConfirm={handleTransferConfirm} onCancel={() => setIsTransferModalOpen(false)} />
            )}

            {/* 従業員削除確認ダイアログ */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div className="bg-white rounded-lg shadow-xl w-[480px] p-6">
                        <h2 className="text-base font-bold text-gray-800 border-b-2 border-red-500 pb-2 mb-4">{t("EMPLOYEE")}の削除</h2>
                        <p className="text-sm text-gray-700 mb-4">{deleteTarget.name}さんを削除します。よろしいですか？</p>
                        <div className="flex justify-end gap-3 mt-6">
                            <Button variant="outline" size="md" onClick={() => setDeleteTarget(null)}>キャンセル</Button>
                            <Button variant="danger" size="md" onClick={handleDeleteConfirm}>削除</Button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

// 異動確認モーダルコンポーネント
interface TransferModalProps {
    data: {
        employee: EmployeeResponse;
        fromDeptName: string;
        toDeptId: number;
        toDeptName: string;
    };
    onConfirm: (employeeId: number, toDeptId: number, date: string) => void;
    onCancel: () => void;
}

const TransferModal = ({ data, onConfirm, onCancel }: TransferModalProps) => {
    const { t } = useTranslation("common");
    const [date, setDate] = useState("");
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-2xl w-[400px] overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b font-bold text-gray-800 tracking-wider">{t("EMPLOYEE")}の異動</div>
                <div className="p-6 space-y-4 text-sm">
                    <div className="grid grid-cols-3 gap-y-2">
                        <span className="text-gray-500">{t("EMPLOYEE")}</span><span className="col-span-2 font-bold">{data.employee.name}</span>
                        <span className="text-gray-500">移動元</span><span className="col-span-2">{data.fromDeptName}</span>
                        <span className="text-gray-500">異動先</span><span className="col-span-2 text-brand-600 font-bold">{data.toDeptName}</span>
                    </div>
                    <div className="mt-4">
                        <label className="block text-xs font-bold text-gray-600 mb-1 uppercase">異動日</label>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full border-2 border-gray-200 p-2 rounded focus:border-brand-500 outline-none transition-colors" />
                    </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                    <Button variant="outline" size="md" onClick={onCancel}>キャンセル</Button>
                    <Button variant="primary" size="md" onClick={() => onConfirm(data.employee.id, data.toDeptId, date)} disabled={!date} className="px-8 shadow-md">登録</Button>
                </div>
            </div>
        </div>
    );
};

// --- アイコン等 ---
const ListIcon = (p: React.SVGProps<SVGSVGElement>) => <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>;
const GridIcon = (p: React.SVGProps<SVGSVGElement>) => <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" /></svg>;

export default EmployeesPage;